import { useMemo } from 'react';
// @mui
import { CssBaseline } from '@mui/material';
import {
    createTheme,
    ThemeOptions,
    StyledEngineProvider,
    ThemeProvider as MUIThemeProvider,
} from '@mui/material/styles';
import palette from './palette';
import typography from './typography';
import shadows from './shadows';
import customShadows from './customShadows';
import componentsOverride from './overrides';
import GlobalStyles from './globalStyles';
import { Directions } from '../common';
import { useAppSelector } from '../base';
import { userSelectors } from '../actions';

// ----------------------------------------------------------------------

declare module '@mui/material/styles' {
    interface Theme {
        componentSizes: {
            xsmall: number;
            small: number;
            medium: number;
            large: number;
        };
    }
    interface ThemeOptions {
        componentSizes?: {
            xsmall: number;
            small: number;
            medium: number;
            large: number;
        };
    }
}

interface Props {
    children: React.ReactNode;
    themeDirection?: Directions.ltr | Directions.rtl;
    themeMode?: 'light' | 'dark';
}

export default function ThemeProvider(props: Props) {
    const { children, themeDirection, themeMode = 'light' } = props;

    const pageDirection = useAppSelector(userSelectors.getPageDirection);

    const themeOptions: ThemeOptions = useMemo(
        () => ({
            palette: palette(themeMode),
            typography,
            shape: { borderRadius: 16 },
            direction: themeDirection || pageDirection,
            shadows: shadows(themeMode),
            customShadows: customShadows(themeMode),
            components: {
                MuiButtonBase: {
                    defaultProps: {
                        size: 'small',
                    },
                },
            },
            componentSizes: {
                xsmall: 40,
                small: 48,
                medium: 56,
                large: 64,
            },
        }),
        [themeDirection, themeMode, pageDirection]
    );

    const theme = createTheme(themeOptions);

    theme.components = componentsOverride(theme);

    return (
        <StyledEngineProvider injectFirst>
            <MUIThemeProvider theme={theme}>
                <CssBaseline />
                <GlobalStyles />
                {children}
            </MUIThemeProvider>
        </StyledEngineProvider>
    );
}
